import React, { useMemo } from "react";
import { DataSourcesStore } from "../stores/DataSourcesStore";
import DataSourceContainer from "../components/DataSourceContainer";
import ConcatField from "./ConcatField";
import AddIcon from "@mui/icons-material/Add";
import AcxButton from "components/UI/AcxButton";
import { v4 as uuidv4 } from "uuid";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";
import { Box } from "@mui/material";
import { emptyConcatField } from "../helpers/emptyStates";

type ConcatFieldsProps = {
    store: DataSourcesStore;
};

const ConcatFields: React.FC<ConcatFieldsProps> = observer(({ store }) => {
    const {
        concatFields,
        setConcatFields,
        sourceFields,
        updateSelectedTargetFields,
        getUpdatedTargetFields,
    } = store;

    const updateConcatFields = (
        id: string,
        key: string,
        value: string,
        targetFieldId: string,
    ) => {
        if (key === "targetFieldName") {
            updateSelectedTargetFields({
                targetFieldId,
                action: value ? "add" : "remove",
                sourceFieldId: id,
            });
        }

        setConcatFields(
            concatFields.map((field) => {
                if (field.id === id) {
                    return {
                        ...field,
                        [key]: value,
                    };
                }
                return field;
            }),
        );
    };

    const updateField = (fieldId: string, id: string, value: string) => {
        setConcatFields(
            concatFields.map((field) => {
                if (field.id === fieldId) {
                    return {
                        ...field,
                        fields: field.fields.map((f) => {
                            if (f.id === id) {
                                return {
                                    ...f,
                                    value,
                                };
                            }
                            return f;
                        }),
                    };
                }
                return field;
            }),
        );
    };

    const addNewConcatField = () => {
        setConcatFields([...concatFields, emptyConcatField()]);
    };

    const removeConcatField = (id: string) => {
        updateSelectedTargetFields({ sourceFieldId: id, action: "remove" });
        setConcatFields(concatFields.filter((field) => field.id !== id));
    };

    const sourceFieldOptions = useMemo(
        () =>
            sourceFields
                .map((field) => ({
                    label: field.alias,
                    value: field.alias,
                }))
                .filter((f) => f.value),
        [sourceFields],
    );

    const addField = (id: string, type: "field" | "separator") => {
        setConcatFields(
            concatFields.map((field) => {
                if (field.id === id) {
                    return {
                        ...field,
                        fields: [
                            ...field.fields,
                            { id: uuidv4(), type, value: "" },
                        ],
                    };
                }
                return field;
            }),
        );
    };

    const removeField = (fieldId: string, id: string) => {
        setConcatFields(
            concatFields.map((field) => {
                if (field.id === fieldId) {
                    return {
                        ...field,
                        fields: field.fields.filter((f) => f.id !== id),
                    };
                }
                return field;
            }),
        );
    };

    return (
        <DataSourceContainer
            heading="Concatenate Fields"
            subHeading="Combine multiple fields into a single output"
        >
            <Box
                sx={{
                    gap: theme.spacing(2),
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {concatFields.map((field) => {
                    return (
                        <ConcatField
                            key={field.id}
                            field={field}
                            sourceFieldOptions={sourceFieldOptions}
                            removeConcatField={() =>
                                removeConcatField(field.id)
                            }
                            getUpdatedTargetFields={getUpdatedTargetFields}
                            updateConcatFields={(key, value, targetFieldId) =>
                                updateConcatFields(
                                    field.id,
                                    key,
                                    value,
                                    targetFieldId,
                                )
                            }
                            updateField={(id, value) =>
                                updateField(field.id, id, value)
                            }
                            addField={(type) => addField(field.id, type)}
                            removeField={(id) => removeField(field.id, id)}
                        />
                    );
                })}
            </Box>
            <AcxButton
                startIcon={<AddIcon />}
                onClick={addNewConcatField}
                color="white"
                sx={{
                    color: theme.palette.primary.main,
                    marginTop: theme.spacing(2),
                }}
            >
                Field
            </AcxButton>
        </DataSourceContainer>
    );
});

export default ConcatFields;
